import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, ChevronUp } from 'lucide-react';
import SearchStatus from './SearchStatus';
import { getTasksBySection, getTaskUrl, groupTasksBySubcategory, Task } from '../constants/tasks';

interface TaskSectionPageProps {
  section: Task['section'];
  title: string;
  description: string;
}

const TaskSectionPage: React.FC<TaskSectionPageProps> = ({ section, title, description }) => {
  const navigate = useNavigate();
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;
  const [completedTasks, setCompletedTasks] = useState<Set<string>>(new Set());
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>(() => {
    if (!isMobile) return 'All';
    return sessionStorage.getItem(`anglopi-topic-${section}`) || '';
  });
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    try {
      const saved = sessionStorage.getItem(`anglopi-expanded-topics-${section}`);
      return new Set(saved ? JSON.parse(saved) : []);
    } catch {
      return new Set();
    }
  });

  const sectionTasks = useMemo(() => getTasksBySection(section), [section]);
  const groupedTasks = useMemo(() => groupTasksBySubcategory(sectionTasks), [sectionTasks]);
  const totalTasks = sectionTasks.length;

  const subcategoryCards = useMemo(
    () => [{ label: 'All', count: totalTasks }].concat(groupedTasks.map(group => ({ label: group.subcategory, count: group.tasks.length }))),
    [groupedTasks, totalTasks]
  );

  useEffect(() => {
    if (!selectedSubcategory && groupedTasks.length > 0) {
      setSelectedSubcategory(groupedTasks[0].subcategory);
    }
  }, [groupedTasks, selectedSubcategory]);

  useEffect(() => {
    if (isMobile && selectedSubcategory) {
      sessionStorage.setItem(`anglopi-topic-${section}`, selectedSubcategory);
    }
  }, [isMobile, section, selectedSubcategory]);

  const visibleGroups = selectedSubcategory === 'All'
    ? groupedTasks
    : groupedTasks.filter(group => group.subcategory === selectedSubcategory);

  useEffect(() => {
    const savedCompletedTasks = localStorage.getItem('anglopi-completed-tasks');
    if (savedCompletedTasks) {
      try {
        const parsed = JSON.parse(savedCompletedTasks);
        setCompletedTasks(new Set(parsed));
      } catch (error) {
        console.error('Error loading completed tasks:', error);
      }
    }
  }, []);

  const handleTaskSelect = (task: Task) => {
    navigate(getTaskUrl(task));
  };

  const toggleGroupExpansion = (subcategory: string) => {
    setExpandedGroups((current) => {
      const next = new Set(current);
      if (next.has(subcategory)) {
        next.delete(subcategory);
      } else {
        next.add(subcategory);
      }
      sessionStorage.setItem(
        `anglopi-expanded-topics-${section}`,
        JSON.stringify(Array.from(next))
      );
      return next;
    });
  };

  return (
    <div className="content-area">
      <SearchStatus />

      <div className="section-header">
        <h1>{title}</h1>
        <p>{description}</p>
      </div>

      <div className="subcategory-filter">
        <div className="subcategory-filter-header">
          <h2 style={{ margin: 0 }}>Choose a topic</h2>
          <div className="subcategory-filter-controls">
            <span style={{ color: 'var(--color-text-muted)' }}>{totalTasks} tasks</span>
          </div>
        </div>
        <div className="subcategory-grid" role="group" aria-label="Task topics">
          {subcategoryCards.map((subcategory) => {
            const isActive = selectedSubcategory === subcategory.label;
            return (
              <button
                key={subcategory.label}
                type="button"
                onClick={() => setSelectedSubcategory(subcategory.label)}
                className={`subcategory-card ${isActive ? 'active' : ''}`}
                aria-pressed={isActive}
              >
                <div className="subcategory-card-inner">
                  <span className="subcategory-label">{subcategory.label}</span>
                </div>
                <div className="subcategory-count">{subcategory.count} tasks</div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="task-groups">
        {visibleGroups.map((group) => (
          <div
            key={group.subcategory}
            className={`task-group ${expandedGroups.has(group.subcategory) ? 'is-expanded' : ''}`}
          >
            <div className="task-group-header">
              <h2 style={{ marginBottom: 'var(--spacing-xs)' }}>{group.subcategory}</h2>
              <span className="task-group-count">{group.tasks.length} tasks</span>
            </div>
            <div className="task-grid">
              {group.tasks.map((task) => (
                <button
                  type="button"
                  key={task.id}
                  className={`task-card difficulty-${task.difficulty.toLowerCase()} ${completedTasks.has(task.id) ? 'completed' : ''}`}
                  onClick={() => handleTaskSelect(task)}
                >
                  <div className="task-card-header">
                    <h3>{task.title}</h3>
                    <div className="task-badges">
                      {completedTasks.has(task.id) && <span className="completed-badge">✓</span>}
                      <span className={`difficulty-badge difficulty-${task.difficulty.toLowerCase()}`}>
                        Level {task.level} - {task.difficulty}
                      </span>
                    </div>
                  </div>
                  <p className="task-description">{task.description}</p>
                  <div className="task-meta">
                    {completedTasks.has(task.id) && <span className="completed-text">Completed</span>}
                  </div>
                  <ChevronRight className="task-card-chevron" size={20} aria-hidden />
                </button>
              ))}
            </div>
            {group.tasks.length > 5 && (
              <button
                type="button"
                className="task-list-toggle"
                onClick={() => toggleGroupExpansion(group.subcategory)}
                aria-expanded={expandedGroups.has(group.subcategory)}
              >
                {expandedGroups.has(group.subcategory) ? (
                  <>
                    Show fewer tasks <ChevronUp size={17} aria-hidden />
                  </>
                ) : (
                  <>
                    Show all {group.tasks.length} tasks <ChevronDown size={17} aria-hidden />
                  </>
                )}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default TaskSectionPage;
